import React from 'react';
import { Box, Typography, Chip, Divider, Stack, Tooltip } from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import {
  LocalHospital as HospitalIcon,
  Emergency as EmergencyIcon,
  Medication as MedicationIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import { DischargeNinetyDaySummary as SummaryT, DischargeClinicalEvent } from '~/types/discharge';
import ClinicalNarrative from '~/components/patient-board/ClinicalNarrative';
import DischargedToStat from '~/components/patient-board/DischargedToStat';

interface Props {
  summary: SummaryT;
}

const DischargeNinetyDaySummary: React.FC<Props> = ({ summary }) => {
  const getTrendIcon = () => {
    switch (summary.trend) {
      case 'improving': return <TrendingUpIcon sx={{ color: 'success.main' }} />;
      case 'declining': return <TrendingDownIcon sx={{ color: 'error.main' }} />;
      case 'stable':    return <TrendingFlatIcon sx={{ color: 'warning.main' }} />;
    }
  };

  const getTrendColor = (): 'success' | 'error' | 'warning' => {
    switch (summary.trend) {
      case 'improving': return 'success';
      case 'declining': return 'error';
      case 'stable':    return 'warning';
    }
  };

  const getEventIcon = (type: DischargeClinicalEvent['type']) => {
    switch (type) {
      case 'admission':
      case 'discharge':         return <HospitalIcon />;
      case 'er_visit':          return <EmergencyIcon />;
      case 'medication_change': return <MedicationIcon />;
      default:                  return <AssessmentIcon />;
    }
  };

  const getEventColor = (type: DischargeClinicalEvent['type']): 'primary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (type) {
      case 'admission': return 'primary';
      case 'er_visit':  return 'error';
      case 'discharge': return 'success';
      case 'procedure': return 'warning';
      default:          return 'info';
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });

  // Most recent first. Seeded demo data is not consistently ordered, so the
  // timeline sorts rather than trusting the array order.
  const eventsNewestFirst = [...summary.events].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <Box>
      {/* Summary statistics row */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, px: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
              Admissions
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 600 }}>{summary.recentAdmissions}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
              ER Visits
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 600 }}>{summary.erVisits}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
              Current LOS
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 600 }}>{summary.currentLOS}</Typography>
          </Box>
          <DischargedToStat events={summary.events} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
              Current Inpatient Trend
            </Typography>
            <Tooltip title={summary.trendDescription} arrow placement="top">
              <Chip
                icon={getTrendIcon()}
                label={summary.trend.charAt(0).toUpperCase() + summary.trend.slice(1)}
                color={getTrendColor()}
                sx={{ fontWeight: 600, cursor: 'pointer', mt: 0.5 }}
              />
            </Tooltip>
          </Box>
        </Box>

        {/* Narrative */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
              Clinical Summary:
            </Typography>
            <Typography variant="caption" color="grey.500" sx={{ fontStyle: 'italic' }}>
              Last updated {formatDateTime(summary.lastUpdated)}
            </Typography>
          </Box>
          <Divider sx={{ mb: 3, borderBottomWidth: 2 }} />
          <ClinicalNarrative text={summary.narrativeSummary} references={summary.references} />
        </Box>
      </Box>

      {/* Key findings */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
          Key Findings
        </Typography>
        <Divider sx={{ mt: 1, mb: 3, borderBottomWidth: 2 }} />
        <Stack spacing={2}>
          {summary.keyFindings.map((finding, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.75, mr: 1.5, flexShrink: 0 }} />
              <Typography variant="body1">{finding}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Events timeline */}
      <Box>
        <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
          Recent Events Timeline
        </Typography>
        <Divider sx={{ mt: 1, mb: 3, borderBottomWidth: 2 }} />
        <Timeline sx={{ mt: 2 }}>
          {eventsNewestFirst.map((event, i) => (
            <TimelineItem key={i}>
              <TimelineOppositeContent sx={{ flex: 0.3 }} color="grey.500">
                <Typography variant="body2">{formatDate(event.date)}</Typography>
                {event.location && (
                  <Typography variant="caption" color="grey.500">{event.location}</Typography>
                )}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot color={getEventColor(event.type)}>{getEventIcon(event.type)}</TimelineDot>
                {i < eventsNewestFirst.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {event.type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </Typography>
                <Typography variant="body2" color="grey.500">
                  {event.description}
                </Typography>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Box>
    </Box>
  );
};

export default DischargeNinetyDaySummary;
